import React from "react";
import { Autocomplete, Box, Button, Checkbox, Chip, TextField, Typography } from "@mui/material";
import { Delete, People } from "@mui/icons-material";
import type { Participant, Study } from "../../../types";
import { formatPhoneNumber } from "../../../utils/textUtils";

export type InviteParticipant = {
  email: string;
  phoneNumber: string;
};

export type ParticipantInviteFormState = {
  studyName: string;
  description: string;
  duration: string;
  participants: InviteParticipant[];
};

export type ParticipantContactFormState = {
  emailTitle: string;
  content: string;
  recipients: string[];
};

interface FormProps<T> {
  form: T;
  setForm: React.Dispatch<React.SetStateAction<T>>;
}

export function ParticipantInviteForm({ form, setForm, study }: FormProps<ParticipantInviteFormState> & {
  study: Pick<Study, "name" | "description"> | null;
}) {
  return (
    <>
      <TextField
        label="Study Name"
        fullWidth
        variant="outlined"
        value={form.studyName}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            studyName: e.target.value,
          }))
        }
        placeholder={study?.name}
        helperText="Leave blank to use default study name"
      />

      <TextField
        label="Description"
        fullWidth
        multiline
        rows={3}
        variant="outlined"
        value={form.description}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            description: e.target.value,
          }))
        }
        placeholder={study?.description || "Enter study description"}
        helperText="Leave blank to use default study description"
      />

      <TextField
        label="Duration"
        fullWidth
        variant="outlined"
        value={form.duration}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            duration: e.target.value,
          }))
        }
        placeholder="e.g., 4 weeks, 2 months"
      />

      <ParticipantInviteList
        participants={form.participants}
        onChange={(participants) => setForm((prev) => ({ ...prev, participants }))}
      />
    </>
  );
}

export function ParticipantContactForm({ form, setForm, participants }: FormProps<ParticipantContactFormState> & {
  participants: Participant[];
}) {
  return (
    <>
      <TextField
        label="Email Title"
        fullWidth
        variant="outlined"
        value={form.emailTitle}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            emailTitle: e.target.value,
          }))
        }
        placeholder="Enter email subject"
        required
      />

      <TextField
        label="Email Content"
        fullWidth
        multiline
        rows={6}
        variant="outlined"
        value={form.content}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            content: e.target.value,
          }))
        }
        placeholder="Enter your message to participants..."
        required
      />

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          Recipients
        </Typography>

        {participants.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Select from existing participants:
            </Typography>
            <Box
              sx={{
                maxHeight: 200,
                overflowY: "auto",
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                p: 1,
              }}
            >
              {participants.map((participant) => (
                <Box
                  key={participant.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    py: 0.5,
                  }}
                >
                  <Checkbox
                    checked={form.recipients.includes(
                      participant.participantId,
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setForm((prev) => ({
                          ...prev,
                          recipients: [
                            ...prev.recipients,
                            participant.participantId,
                          ],
                        }));
                      } else {
                        setForm((prev) => ({
                          ...prev,
                          recipients: prev.recipients.filter(
                            (id) => id !== participant.participantId,
                          ),
                        }));
                      }
                    }}
                  />
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {participant.participantId}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={form.recipients.filter(
            (recipient) =>
              !participants.some((p) => p.participantId === recipient),
          )}
          onChange={(event, newValue) => {
            const validEmails = newValue.filter((email: string) => {
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              return emailRegex.test(email);
            });
            const existingParticipants = form.recipients.filter((recipient) =>
              participants.some((p) => p.participantId === recipient),
            );
            setForm((prev) => ({
              ...prev,
              recipients: [...existingParticipants, ...validEmails],
            }));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Additional Email Addresses"
              placeholder="Enter additional email addresses (press Enter after each)"
              helperText="Enter valid email addresses for non-participants"
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={option}
                {...getTagProps({ index })}
                key={index}
              />
            ))
          }
        />
      </Box>
    </>
  );
}

export function ParticipantInviteList({ participants, onChange }: {
  participants: InviteParticipant[];
  onChange: (participants: InviteParticipant[]) => void;
}) {
  const updateParticipant = (index: number, updates: Partial<InviteParticipant>) => {
    onChange(participants.map((participant, i) => i === index ? { ...participant, ...updates } : participant));
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Add Participants
      </Typography>
      {participants.map((participant, index) => (
        <Box
          key={index}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mb: 2,
          }}
        >
          {index === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              * At least one participant is required
            </Typography>
          )}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "flex-start",
            }}
          >
            <TextField
              fullWidth
              label="Email Address"
              value={participant.email}
              onChange={(e) => updateParticipant(index, { email: e.target.value })}
              placeholder="participant@example.com"
              required
              type="email"
              autoFocus={index === 0}
              variant="outlined"
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
            <TextField
              fullWidth
              label="Phone Number (Optional)"
              value={participant.phoneNumber}
              onChange={(e) => updateParticipant(index, { phoneNumber: formatPhoneNumber(e.target.value) })}
              placeholder="(000)-0000-000"
              variant="outlined"
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
            {participants.length > 1 && index > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "56px",
                  width: "110px",
                  cursor: "pointer",
                  flexShrink: 0,
                  "&:hover": {
                    opacity: 0.7,
                  },
                  transition: "opacity 0.2s",
                }}
                onClick={() => onChange(participants.filter((_, i) => i !== index))}
                title="Remove participant"
              >
                <Delete sx={{ color: "error.main", fontSize: "1.5rem" }} />
              </Box>
            ) : null}
            {index === 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 1.5,
                  py: 1,
                  backgroundColor: "primary.light",
                  borderRadius: 1,
                  height: "56px",
                  width: "110px",
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="caption"
                  color="primary.contrastText"
                  sx={{ fontWeight: 600 }}
                >
                  Required
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      ))}
      <Button
        variant="outlined"
        startIcon={<People />}
        onClick={() => onChange([...participants, { email: "", phoneNumber: "" }])}
        sx={{ mb: 2 }}
      >
        Add Another Participant
      </Button>
    </Box>
  );
}
